// Adapted from apps/admin/src/pages/dashboard/gps-tracking/components/route-points-table.tsx (PRD
// #875): govdash has no user card to link the driver to, nor saved column settings.
import React, { useMemo } from "react";
import { cn, Column, DataTable } from "@racoon/ui";
import { format } from "date-fns";
import { useT, Namespace } from "@racoon/i18n";
import type { RouteTimelineSegment } from "@racoon/ui";

interface RouteSegmentsTableProps {
  data: RouteTimelineSegment[];
  loading?: boolean;
  /** Tag every row with the timestamps it was derived from (GPS / fact). */
  showSource?: boolean;
}

const EMPTY_PLACEHOLDER = "—";
const DATE_FORMAT = "HH:mm dd.MM.yyyy";
const MS_PER_HOUR = 1000 * 60 * 60;
const MS_PER_MINUTE = 1000 * 60;

// Helper function to format date
const formatDate = (timestamp: number): string => {
  if (!timestamp) return EMPTY_PLACEHOLDER;
  return format(new Date(timestamp), DATE_FORMAT);
};

export const RouteSegmentsTable: React.FC<RouteSegmentsTableProps> = ({
  data,
  loading = false,
  showSource = false,
}) => {
  const t = useT();

  const getRowKey = (row: RouteTimelineSegment) => {
    return `${row.source}-${row.startTime}-${row.label}`;
  };

  const columns: Column<RouteTimelineSegment>[] = useMemo(
    () => [
      {
        id: "label",
        header: t(Namespace.GpsTracking, "intervalColumn"), // Same header the route table used, for consistency
        key: "label",
        width: 300,
        accessor: (row) => row.label,
        cell: showSource
          ? ({ row }) => (
              <span className="flex items-center gap-2">
                <span
                  className={cn(
                    "shrink-0 px-1.5 py-0.5 rounded text-[11px] font-medium",
                    row.original.source === "gps"
                      ? "bg-brand-blue/10 text-brand-blue"
                      : "bg-gray-100 text-gray-600",
                  )}
                >
                  {t(
                    Namespace.GpsTracking,
                    row.original.source === "gps" ? "sourceGps" : "sourceFact",
                  )}
                </span>
                <span className="truncate">{row.original.label}</span>
              </span>
            )
          : undefined,
      },
      {
        id: "driver",
        header: t(Namespace.GpsTracking, "driverColumn"),
        key: "driverName",
        sortable: true,
        width: 180,
        accessor: (row) => row.driverName || EMPTY_PLACEHOLDER,
      },
      {
        id: "start",
        header: t(Namespace.GpsTracking, "startColumn"),
        sortable: true,
        width: 150,
        accessor: (row) => formatDate(row.startTime),
        sortFn: (a, b) => a.startTime - b.startTime,
      },
      {
        id: "end",
        header: t(Namespace.GpsTracking, "endColumn"),
        sortable: true,
        width: 150,
        accessor: (row) => formatDate(row.endTime),
        sortFn: (a, b) => a.endTime - b.endTime,
      },
      {
        id: "duration",
        header: t(Namespace.GpsTracking, "durationColumn"),
        sortable: true,
        width: 130,
        accessor: (row) => {
          const durationMs = row.endTime - row.startTime;
          const hours = Math.floor(durationMs / MS_PER_HOUR);
          const minutes = Math.floor((durationMs % MS_PER_HOUR) / MS_PER_MINUTE);

          if (hours === 0) {
            return `${minutes} ${t(Namespace.Routes, "minutes")}`;
          }
          return `${hours} ${t(Namespace.Routes, "hours")}, ${minutes} ${t(Namespace.Routes, "minutes")}`;
        },
        sortFn: (a, b) => a.endTime - a.startTime - (b.endTime - b.startTime),
      },
    ],
    [t, showSource],
  );

  return (
    <div data-testid="gps-tracking-segments" className="w-full flex flex-1 flex-col min-h-0">
      <DataTable
        columns={columns}
        data={data}
        loading={loading}
        rowKey={getRowKey}
        initialSort={{ id: "start", dir: "asc" }} // Chronological order makes sense for route
        stickyHeader
        empty={
          <div className="p-6 text-sm text-muted-foreground text-center">
            {t(Namespace.Trucks, "tableNoData")}
          </div>
        }
        namespace={Namespace.GpsTracking}
      />
    </div>
  );
};
